import { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card, CardHeader, TrendPill } from '../../../components/ui/Card';
import { Nil, Table, THead, Th, Td, Tr } from '../../../components/ui/Table';
import { EmptyState, ErrorState, LoadingState } from '../../../components/ui/States';
import { useAccess } from '../../../hooks/useAccess';
import { useSubscriptionsSummary } from '../../../hooks/useFinance';
import { useCsmDeclining, useCsmRoster } from '../../../hooks/useDashboard';
import type { ContractRow } from '../../../api/finance';
import { formatDayMonthYear } from '../../../utils/dateFormat';
import { formatMoney } from '../../../utils/numberFormat';
import { planDisplayName } from '../../ProjectManagement/Finance/formatters';
import { DecliningTab, pctText, triggerOf, type DeclineTrigger, type DecliningRow } from './DecliningTab';
import { AttioCompanyLink, companyKey } from './attio';
import { TOKENS_LABEL } from './decliningGroups';

const RENEWAL_HORIZON_DAYS = 90;

/** Status colour only when it matters: red under 30 days, amber under 60, plain text otherwise. */
function daysLeftClass(daysLeft: number | null): string {
  if (daysLeft === null) return 'text-text-secondary';
  if (daysLeft < 30) return 'font-medium text-error';
  if (daysLeft < 60) return 'font-medium text-warning-dark';
  return 'text-text-secondary';
}

/**
 * The Finance API now names a contract by its Attio company (when the Odoo
 * partner links to one) and sends the Odoo legal name alongside; older APIs
 * send only the Odoo name.
 */
type NamedContract = ContractRow & { odooName?: string | null };

interface DueRow {
  contract: NamedContract;
  /** The usage decline flagged for the same company, if any. */
  decline: { name: string; trigger: DeclineTrigger | null } | null;
  /** Logins down while tokens rose (the detector's watch list): shown, but not a decline. */
  watch: { trigger: DeclineTrigger | null; tokensChangePct: number | null } | null;
  /** Attio company record, matched on the company name (Odoo contracts carry no Attio id). */
  attioRecordId: string | null;
}

/**
 * Contracts expiring in the next 90 days, joined to the decline detector and the
 * Attio roster by company name. Odoo contracts carry no Attio id, so the name is
 * the only key; a contract whose name doesn't match stays unlinked.
 */
function useRenewalsAtRisk() {
  const subs = useSubscriptionsSummary();
  const declining = useCsmDeclining();
  const roster = useCsmRoster(true);

  const rows = useMemo<DueRow[]>(() => {
    const declineByKey = new Map<string, DueRow['decline']>();
    for (const c of (declining.data?.companies ?? []) as DecliningRow[]) {
      const key = companyKey(c.companyName);
      if (key) declineByKey.set(key, { name: c.companyName ?? '', trigger: triggerOf(c) });
    }
    const watchByKey = new Map<string, DueRow['watch']>();
    for (const c of (declining.data?.watch ?? []) as DecliningRow[]) {
      const key = companyKey(c.companyName);
      const t = triggerOf(c);
      if (key) {
        watchByKey.set(key, {
          trigger: t,
          tokensChangePct: t?.window === '7d' ? c.weeklyTokensChangePct : c.monthlyTokensChangePct,
        });
      }
    }
    const attioByKey = new Map<string, string>();
    for (const r of roster.data?.rows ?? []) {
      const key = companyKey(r.companyName);
      if (key && !attioByKey.has(key)) attioByKey.set(key, r.attioRecordId);
    }
    return (subs.data?.summary.contracts ?? [])
      .filter((c) => c.expiryDate !== null && c.daysLeft !== null && c.daysLeft >= 0 && c.daysLeft <= RENEWAL_HORIZON_DAYS)
      .map((contract) => {
        const named = contract as NamedContract;
        const keys = [companyKey(named.companyName), companyKey(named.odooName)].filter(Boolean);
        const pick = <T,>(m: Map<string, T>) => keys.map((k) => m.get(k)).find((v) => v !== undefined) ?? null;
        return { contract: named, decline: pick(declineByKey), watch: pick(watchByKey), attioRecordId: pick(attioByKey) };
      })
      // Renewing companies that are also losing usage come first, then soonest expiry.
      .sort((a, b) => {
        if (!!a.decline !== !!b.decline) return a.decline ? -1 : 1;
        return (a.contract.daysLeft ?? 0) - (b.contract.daysLeft ?? 0);
      });
  }, [subs.data, declining.data, roster.data]);

  return { subs, declining, rows };
}

/** One line per company that renews within 90 days while its usage is declining. */
function RenewalDeclineStrip({ rows, declineFailed }: { rows: DueRow[]; declineFailed: boolean }) {
  const hits = rows.filter((r) => r.decline);
  if (declineFailed) {
    return (
      <p className="text-body-xs text-text-tertiary">
        Renewal × decline check unavailable: the declining list couldn’t load.
      </p>
    );
  }
  if (hits.length === 0) return null;
  return (
    <div className="rounded-2xl border border-error/30 bg-error-light/40 px-4 py-3">
      <div className="flex items-start gap-2 text-body-sm text-text-primary">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-error" aria-hidden />
        <p>
          <span className="font-medium">
            {hits.length} {hits.length === 1 ? 'customer renews' : 'customers renew'} within {RENEWAL_HORIZON_DAYS} days
            while usage declines:
          </span>{' '}
          {hits.map((r, i) => (
            <span key={r.contract.id}>
              {i > 0 && '; '}
              {r.contract.companyName} renews in {r.contract.daysLeft} {r.contract.daysLeft === 1 ? 'day' : 'days'}
              {r.decline?.trigger && (
                <>
                  {' '}
                  at {pctText(r.decline.trigger.changePct)} {r.decline.trigger.metric === 'tokens' ? 'tokens' : 'login events'}
                </>
              )}
            </span>
          ))}
          .
        </p>
      </div>
    </div>
  );
}

function RenewalsDue({ query, rows }: { query: ReturnType<typeof useSubscriptionsSummary>; rows: DueRow[] }) {
  const unlinked = rows.filter((r) => !r.attioRecordId).length;
  return (
    <Card padding="sm">
      <CardHeader
        title={`Renewals due in the next ${RENEWAL_HORIZON_DAYS} days`}
        description={`Contract expiries from Odoo, declining customers first. The full expiry timeline lives in Finance.${
          unlinked > 0 ? ` ${unlinked} without a matching Attio company, so no link.` : ''
        }`}
      />
      {query.isLoading ? (
        <LoadingState />
      ) : query.error ? (
        <ErrorState title="Couldn’t load contract expiries" error={query.error} onRetry={() => query.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title={`No contract expires in the next ${RENEWAL_HORIZON_DAYS} days`} compact />
      ) : (
        <Table minWidth={720}>
          <THead>
            <tr>
              <Th>Company</Th>
              <Th>Plan</Th>
              <Th align="right">MRR</Th>
              <Th align="right" title="The decline detector's change: login events, or tokens (Classic + aOS)">Usage</Th>
              <Th align="right">Expires</Th>
              <Th align="right">Days left</Th>
              <Th className="w-10 !pr-0">
                <span className="sr-only">Attio</span>
              </Th>
            </tr>
          </THead>
          <tbody>
            {rows.map(({ contract: c, decline, watch, attioRecordId }) => (
              <Tr key={c.id}>
                <Td>
                  <div className="font-medium text-text-primary">{c.companyName}</div>
                  {c.odooName && c.odooName !== c.companyName && (
                    <div className="text-body-xs text-text-tertiary">{c.odooName}</div>
                  )}
                </Td>
                <Td className="text-text-secondary">{planDisplayName(c.planName)}</Td>
                <Td align="right" className="tabular-nums">
                  {c.mrrEur === null ? (
                    <span title="Odoo had no rate to convert this contract's currency">
                      {formatMoney(c.sourceMrr, `${c.sourceCurrency} `)}
                    </span>
                  ) : (
                    formatMoney(c.mrrEur)
                  )}
                </Td>
                <Td align="right">
                  {decline?.trigger ? (
                    <span title={`${decline.trigger.metric === 'tokens' ? TOKENS_LABEL : 'Login events'}, ${decline.trigger.window}`}>
                      <TrendPill trend="down">{pctText(decline.trigger.changePct)}</TrendPill>
                    </span>
                  ) : watch?.trigger ? (
                    // Arendt, 12 Oct: login events −41% while tokens rose 1,261%, on the watch list, not a decline.
                    <span
                      className="cursor-help whitespace-nowrap text-body-xs text-text-secondary"
                      title={`Watch, not a decline: login events ${pctText(watch.trigger.changePct)} while tokens (Classic + aOS) ${pctText(watch.tokensChangePct)}, ${watch.trigger.window}. No health penalty, no alert.`}
                    >
                      Login events {pctText(watch.trigger.changePct)} · tokens {pctText(watch.tokensChangePct)}
                    </span>
                  ) : (
                    <Nil />
                  )}
                </Td>
                <Td align="right" className="whitespace-nowrap text-text-secondary">
                  {formatDayMonthYear(c.expiryDate)}
                </Td>
                <Td align="right" className={`tabular-nums ${daysLeftClass(c.daysLeft)}`}>
                  {c.daysLeft}
                </Td>
                <Td align="right" className="!pr-0">
                  {attioRecordId ? <AttioCompanyLink recordId={attioRecordId} name={c.companyName} /> : null}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

/** CSM › At risk: renewals that coincide with a decline on top, then the declining list and the renewals. */
export function AtRiskTab() {
  const { canAccess } = useAccess();
  // Contract data comes from the Finance API, so renewals only show for people who can open Finance.
  const finance = canAccess('finance');
  return <div className="space-y-6">{finance ? <RenewalsFirst /> : <DecliningTab />}</div>;
}

/** Strip, then declining usage, then the renewals table. */
function RenewalsFirst() {
  const { subs, declining, rows } = useRenewalsAtRisk();
  return (
    <>
      <RenewalDeclineStrip rows={rows} declineFailed={declining.isError} />
      <DecliningTab />
      <RenewalsDue query={subs} rows={rows} />
    </>
  );
}
